/**
 * Aggregator / multi-hop swaps emit many ERC-20 `Transfer` rows; the wallet-scoped import can create
 * several disposal legs for one economic trade. For tax we want legs that match **net wallet flow**:
 * typically **one** token sold → **stables** (or CRO) received.
 *
 * This pass (runs before EUR allocation in `applyCronosHistoricPrices`):
 * 1. **Intermediate CRO/WCRO hop:** If the tx is swap-like, has an incoming **USD stable** acquisition,
 *    and has both a **CRO/WCRO disposal** and another **non–CRO-like** disposal, the CRO/WCRO leg is
 *    routing — reclassify to `IGNORE_NON_TAX` (not the user’s economic sell).
 * 2. **Single primary sell:** If the tx is swap-like, has incoming **stables**, and **multiple**
 *    non-stable, non–CRO-like disposals (after step 1), **keep** the disposal with the **largest**
 *    human `amount` and suppress the others (fee / marketplace / duplicate legs). This avoids
 *    hardcoding every protocol token.
 *
 * Optional: `CRONOS_MARKETPLACE_FEE_TOKEN_CONTRACTS` — extra contracts to always suppress when
 * another non-fee disposal exists (escape hatch; defaults empty).
 */

import { detectSwapLikeByFlow } from './cronosSwapProceedsDerivation';
import { groupCronosByRef, WCRO_CONTRACT_LOWER } from './croSwapRatioPricing';
import type { LedgerEvent } from './types';

const STABLE_SYMBOLS = new Set(['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD']);

function envTruthy(name: string): boolean {
  const v =
    typeof process !== 'undefined' && process.env?.[name]
      ? String(process.env[name]).trim().toLowerCase()
      : '';
  return v === '1' || v === 'true' || v === 'yes';
}

export function marketplaceFeeSuppressDisabled(): boolean {
  return envTruthy('CRONOS_MARKETPLACE_FEE_SUPPRESS_DISABLE');
}

function normContract(a: string | undefined): string | null {
  if (!a) return null;
  const t = a.trim().toLowerCase();
  return t.startsWith('0x') && t.length === 42 ? t : null;
}

/** Optional allowlist — suppress these fee-token disposals when another disposal exists (no defaults). */
export function marketplaceFeeTokenContractsFromEnv(): Set<string> {
  const raw =
    typeof process !== 'undefined' && process.env?.CRONOS_MARKETPLACE_FEE_TOKEN_CONTRACTS
      ? process.env.CRONOS_MARKETPLACE_FEE_TOKEN_CONTRACTS
      : '';
  const s = new Set<string>();
  for (const part of raw.split(/[\s,]+/)) {
    const n = normContract(part);
    if (n) s.add(n);
  }
  return s;
}

export function mergedOptionalFeeTokenContracts(): Set<string> {
  const out = new Set<string>();
  marketplaceFeeTokenContractsFromEnv().forEach((x) => {
    out.add(x);
  });
  return out;
}

function mergeFlags(ev: LedgerEvent, extra: string[]): void {
  const cur = ev.reviewFlags ?? [];
  ev.reviewFlags = Array.from(new Set([...cur, ...extra]));
}

function suppressLeg(ev: LedgerEvent, subreason: string, explanationSuffix: string, flag: string): void {
  ev.classification = 'IGNORE_NON_TAX';
  ev.fiTaxCategory = 'IGNORE';
  ev.fiSubreason = subreason;
  ev.explanation = [ev.explanation, explanationSuffix].filter(Boolean).join(' ');
  mergeFlags(ev, [flag]);
}

function isStableAcquisition(ev: LedgerEvent): boolean {
  return (
    ev.source === 'cronos' &&
    ev.kind === 'acquisition' &&
    STABLE_SYMBOLS.has(ev.asset.toUpperCase())
  );
}

function hasIncomingStableAcquisition(sibs: LedgerEvent[]): boolean {
  return sibs.some(isStableAcquisition);
}

/** Disposal of native CRO or WCRO (wrapped) — often an intermediate hop in alt → USDC routes. */
function isCroLikeDisposal(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind !== 'disposal') return false;
  const a = ev.asset.toUpperCase();
  if (a === 'CRO' || a === 'WCRO') return true;
  const c = normContract(ev.tokenContractAddress);
  return c === WCRO_CONTRACT_LOWER;
}

/** Disposal of a token that is not a USD stable (user selling stables is a different pattern). */
function isNonStableDisposal(ev: LedgerEvent): boolean {
  if (ev.source !== 'cronos' || ev.kind !== 'disposal') return false;
  return !STABLE_SYMBOLS.has(ev.asset.toUpperCase());
}

export type MarketplaceProtocolSuppressionStats = {
  disposalsSuppressed: number;
  intermediateCroLegsSuppressed: number;
  secondarySellLegsSuppressed: number;
  optionalFeeTokenLegsSuppressed: number;
};

/**
 * Apply wallet-centric suppression for swap-like Cronos txs (see module comment).
 */
export function applyMarketplaceProtocolDisposalSuppression(
  events: LedgerEvent[],
  optionalFeeTokens?: Set<string>
): { events: LedgerEvent[]; stats: MarketplaceProtocolSuppressionStats } {
  const feeOpt = optionalFeeTokens ?? mergedOptionalFeeTokenContracts();
  const stats: MarketplaceProtocolSuppressionStats = {
    disposalsSuppressed: 0,
    intermediateCroLegsSuppressed: 0,
    secondarySellLegsSuppressed: 0,
    optionalFeeTokenLegsSuppressed: 0,
  };

  const byRef = groupCronosByRef(events);

  for (const [, sibs] of Array.from(byRef.entries())) {
    if (!detectSwapLikeByFlow(sibs)) continue;

    const stableIn = hasIncomingStableAcquisition(sibs);
    const allDisposals = sibs.filter((e) => e.source === 'cronos' && e.kind === 'disposal');
    if (allDisposals.length === 0) continue;

    // Optional env: always drop listed fee-token disposals when another disposal exists.
    if (feeOpt.size > 0 && allDisposals.length >= 2) {
      const feeHits = allDisposals.filter((e) => {
        const c = normContract(e.tokenContractAddress);
        return c != null && feeOpt.has(c);
      });
      const nonFee = allDisposals.filter((e) => {
        const c = normContract(e.tokenContractAddress);
        return c == null || !feeOpt.has(c);
      });
      for (const d of feeHits) {
        if (nonFee.length === 0) continue;
        suppressLeg(
          d,
          'CRONOS_MARKETPLACE_FEE_TOKEN_ALLOWLIST',
          'Suppressed via CRONOS_MARKETPLACE_FEE_TOKEN_CONTRACTS — other disposal leg(s) carry the swap.',
          'marketplace_fee_allowlist_suppressed'
        );
        stats.optionalFeeTokenLegsSuppressed++;
        stats.disposalsSuppressed++;
      }
    }

    const disposalsAfterFee = sibs.filter(
      (e) => e.source === 'cronos' && e.kind === 'disposal' && e.classification !== 'IGNORE_NON_TAX'
    );

    // 1) Intermediate CRO/WCRO disposal when stables arrive and another non-CRO disposal exists.
    if (stableIn && disposalsAfterFee.length >= 2) {
      const croDisposals = disposalsAfterFee.filter(isCroLikeDisposal);
      const nonCroNonStable = disposalsAfterFee.filter((e) => !isCroLikeDisposal(e) && isNonStableDisposal(e));
      if (croDisposals.length > 0 && nonCroNonStable.length > 0) {
        for (const d of croDisposals) {
          suppressLeg(
            d,
            'CRONOS_SWAP_INTERMEDIATE_CRO_LEG',
            'Intermediate CRO/WCRO hop in an aggregator tx — proceeds follow the stable / alt leg.',
            'swap_intermediate_cro_suppressed'
          );
          stats.intermediateCroLegsSuppressed++;
          stats.disposalsSuppressed++;
        }
      }
    }

    const disposalsAfterCro = sibs.filter(
      (e) => e.source === 'cronos' && e.kind === 'disposal' && e.classification !== 'IGNORE_NON_TAX'
    );

    // 2) Multiple non-stable sells + stable in → keep largest human amount (primary economic leg).
    if (stableIn && disposalsAfterCro.length >= 2) {
      const candidates = disposalsAfterCro.filter(
        (e) => isNonStableDisposal(e) && !isCroLikeDisposal(e)
      );
      if (candidates.length >= 2) {
        let best = candidates[0]!;
        for (let i = 1; i < candidates.length; i++) {
          const c = candidates[i]!;
          if (c.amount.gt(best.amount)) best = c;
        }
        for (const d of candidates) {
          if (d === best) continue;
          suppressLeg(
            d,
            'CRONOS_SWAP_SECONDARY_SELL_LEG',
            'Secondary outgoing leg in same tx — larger leg treated as primary sell vs incoming stables.',
            'swap_secondary_sell_leg_suppressed'
          );
          stats.secondarySellLegsSuppressed++;
          stats.disposalsSuppressed++;
        }
      }
    }
  }

  return { events, stats };
}

/** @deprecated No default fee-token list; optional env only. */
export const DEFAULT_MARKETPLACE_FEE_TOKEN_CONTRACTS: readonly string[] = [];

export function mergedMarketplaceFeeTokenContracts(): Set<string> {
  return mergedOptionalFeeTokenContracts();
}
